import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, X, Send, Loader2, Sparkles } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQChat = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `faq-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`);
  const endRef = useRef(null);
  const isRTL = i18n.language?.startsWith('ar');

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{ role: 'assistant', content: t('faq.welcome') }]);
    }
  }, [isOpen, messages.length, t]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || loading) return;

    setMessages((m) => [...m, { role: 'user', content: text }]);
    setInput('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/faq/chat`, {
        session_id: sessionId,
        message: text,
        language: isRTL ? 'ar' : 'fr',
      });
      setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: t('faq.error') }]);
    } finally {
      setLoading(false);
    }
  };

  const suggestions = [
    t('faq.suggestions.q1'),
    t('faq.suggestions.q2'),
    t('faq.suggestions.q3'),
    t('faq.suggestions.q4'),
  ];

  return (
    <>
      {/* Floating Launcher */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          data-testid="faq-chat-launcher"
          aria-label={t('faq.title')}
          className={`fixed bottom-6 ${isRTL ? 'left-6' : 'right-6'} z-50 group`}
        >
          <div className="absolute inset-0 bg-blue-500 rounded-full blur-xl opacity-50 animate-pulse group-hover:opacity-75 transition-opacity" />
          <div className="relative flex items-center gap-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-4 rounded-full shadow-2xl hover:scale-105 transition-transform">
            <Sparkles size={20} className="animate-pulse" />
            <MessageCircle size={22} />
            <span className="hidden sm:inline text-sm font-bold">{t('faq.title')}</span>
          </div>
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div
          data-testid="faq-chat-panel"
          className={`fixed bottom-6 ${isRTL ? 'left-6' : 'right-6'} z-50 w-[calc(100vw-3rem)] sm:w-[400px] h-[600px] max-h-[calc(100vh-3rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col overflow-hidden`}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-5 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 bg-white/20 rounded-full flex items-center justify-center">
                <Sparkles size={18} />
              </div>
              <div>
                <h3 className="font-bold text-sm">{t('faq.title')}</h3>
                <p className="text-xs opacity-90">{t('faq.subtitle')}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="faq-chat-close"
              aria-label={t('common.close')}
              className="p-1.5 hover:bg-white/20 rounded-full transition"
            >
              <X size={18} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-500 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm shadow-sm border border-gray-100'
                  }`}
                >
                  {msg.content}
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex items-center gap-2">
                  <Loader2 size={16} className="animate-spin text-blue-500" />
                  <span className="text-xs text-gray-500">{t('faq.thinking')}</span>
                </div>
              </div>
            )}

            {messages.length <= 1 && !loading && (
              <div className="pt-2">
                <p className="text-xs font-semibold text-gray-500 mb-2 px-1">{t('faq.suggestions.title')}</p>
                <div className="flex flex-wrap gap-2">
                  {suggestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => send(q)}
                      data-testid={`faq-suggestion-${i}`}
                      className="text-xs bg-white text-blue-600 border border-blue-200 px-3 py-1.5 rounded-full hover:bg-blue-50 transition"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="border-t border-gray-100 p-3 bg-white">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder={t('faq.placeholder')}
                data-testid="faq-chat-input"
                disabled={loading}
                className="flex-1 px-4 py-2.5 text-sm border border-gray-200 rounded-full focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 disabled:opacity-50"
              />
              <button
                onClick={() => send()}
                disabled={loading || !input.trim()}
                data-testid="faq-chat-send"
                aria-label={t('faq.send')}
                className="w-10 h-10 flex items-center justify-center bg-blue-500 hover:bg-blue-600 text-white rounded-full transition disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FAQChat;
